import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PhoneOutgoing, ShieldAlert } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Stat, inr, useOutboundVisible } from "@/components/outbound/shared";
import { UploadContacts } from "@/components/outbound/UploadContacts";
import { Campaigns } from "@/components/outbound/Campaigns";
import { ShallyPlanCard } from "@/components/outbound/ShallyPlanCard";
import { CallHistory, Credits, DoNotCall } from "@/components/outbound/Panels";
import { Analytics } from "@/components/outbound/Analytics";
import { CrmLeads } from "@/components/outbound/CrmLeads";

function Readiness({ c }) {
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="ob-readiness">
      <p className="mb-1 flex items-center gap-1 font-semibold"><ShieldAlert className="h-4 w-4" />{c.mode === "mock" ? "Preview: calls are MOCKED — no real phone calls are placed." : "Real calls only to authorized test numbers."}</p>
      <ul className="space-y-0.5">{c.items.map((i) => <li key={i.key} data-testid={`ob-ready-${i.key}`}>{i.done ? "✓" : "○"} {i.label}</li>)}</ul>
    </div>
  );
}

function TestCall() {
  const [m, setM] = useState("");
  const go = () => api.post("/outbound/test-call", { mobile: m, confirm: true }).then((r) => toast.success(`Test call started (${r.data.mode})`)).catch((e) => toast.error(apiErr(e)));
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="ob-test-call">
      <input value={m} onChange={(e) => setM(e.target.value)} placeholder="Authorized test mobile" className="rounded border px-2 py-1 text-sm" data-testid="ob-test-input" />
      <Button size="sm" variant="outline" onClick={go} data-testid="ob-test-start"><PhoneOutgoing className="mr-1 h-4 w-4" />Test call</Button>
    </div>
  );
}

export default function ReceptionistOutbound() {
  const ob = useOutboundVisible();
  if (ob === undefined) return <p className="text-sm text-slate-500" data-testid="ob-loading">Loading…</p>;
  if (!ob) return <Navigate to="/settings/ai-receptionist" replace />;
  return <OutboundPage />;
}

function OutboundPage() {
  const nav = useNavigate();
  const q = useQuery({ queryKey: ["ob", "overview"], queryFn: async () => (await api.get("/outbound/overview")).data, refetchInterval: 8000 });
  if (q.isLoading) return <p className="text-sm text-slate-500" data-testid="ob-loading">Loading…</p>;
  if (q.error) return <p className="rounded-xl bg-rose-50 p-4 text-sm text-rose-800" data-testid="ob-error">{apiErr(q.error)}</p>;
  const o = q.data;
  return (
    <div className="mx-auto max-w-6xl space-y-5" data-testid="ob-page">
      <ShallyPlanCard focus="outbound" />
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h1 className="font-display text-2xl font-bold">Outbound Campaigns</h1>
          <p className="text-xs text-slate-500">Optional add-on, billed from prepaid credits — separate from your inbound AI Receptionist. Service-related calls only.</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => nav("/settings/ai-receptionist")} data-testid="ob-back">AI Receptionist</Button>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        <Stat k="Add-on" v={o.enabled ? "Enabled" : "Not enabled"} id="ob-addon" /><Stat k="Credits available" v={inr(o.wallet.available_paise)} id="ob-balance" tone={o.low_credit ? "border-amber-300" : ""} />
        <Stat k="Rate" v={o.pricing.rate_paise_per_min == null ? "Not set" : `${inr(o.pricing.rate_paise_per_min)}/min`} id="ob-rate" />
        <Stat k="Campaigns" v={o.campaigns} id="ob-camp-count" /><Stat k="Contacts" v={o.contacts} id="ob-contact-count" />
      </div>
      {o.low_credit && <p className="rounded-lg bg-amber-50 p-2 text-xs text-amber-900" data-testid="ob-low-credit">Low credit: new calls stop when credits run out.</p>}
      <Readiness c={o.compliance} />
      {o.enabled && <TestCall />}
      <Tabs defaultValue="campaigns">
        <TabsList className="h-auto flex-wrap">
          <TabsTrigger value="campaigns" data-testid="ob-tab-campaigns">Campaigns</TabsTrigger>
          <TabsTrigger value="upload" data-testid="ob-tab-upload">Upload contacts</TabsTrigger>
          <TabsTrigger value="crm" data-testid="ob-tab-crm">CRM leads & consent</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="ob-tab-analytics">Analytics</TabsTrigger>
          <TabsTrigger value="history" data-testid="ob-tab-history">Call history</TabsTrigger>
          <TabsTrigger value="credits" data-testid="ob-tab-credits">Credits & usage</TabsTrigger>
          <TabsTrigger value="dnc" data-testid="ob-tab-dnc">Do not call</TabsTrigger>
        </TabsList>
        <TabsContent value="campaigns"><Campaigns types={o.types} /></TabsContent>
        <TabsContent value="upload"><UploadContacts types={o.types} /></TabsContent>
        <TabsContent value="crm"><CrmLeads /></TabsContent>
        <TabsContent value="analytics"><Analytics /></TabsContent>
        <TabsContent value="history"><CallHistory /></TabsContent>
        <TabsContent value="credits"><Credits pricing={o.pricing} /></TabsContent>
        <TabsContent value="dnc"><DoNotCall /></TabsContent>
      </Tabs>
    </div>
  );
}
